import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { NormalizedPoint } from '../model';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import { normalizedToPixel } from '@peruselab/structure-editor/domain/bonds';
import type { BlinkMarker } from './drawingPaperRenderModel';

/**
 * The drawing paper's transient "blink" feedback (item 33, DrawingPaper.tsx decomposition:
 * see docs/repo-topology-phase-5-drawing-paper-decomposition.md). A genuine leaf -- its only
 * coupling to the rest of the paper is being invoked from a committed-draft handler with a
 * point, and its `blinkMarkers` being read for rendering.
 */
export function usePaperBlinkMarkers(sizeRef: RefObject<PaperSize>) {
  const blinkTimeoutsRef = useRef<Set<number>>(new Set());
  const [blinkMarkers, setBlinkMarkers] = useState<readonly BlinkMarker[]>([]);

  useEffect(
    () => () => {
      blinkTimeoutsRef.current.forEach((timeout) => globalThis.clearTimeout(timeout));
    },
    []
  );

  const addBlinkMarker = useCallback(
    (point: NormalizedPoint) => {
      const id = globalThis.crypto?.randomUUID?.() ?? `blink-${Date.now()}`;
      const marker = { id, ...normalizedToPixel(point, sizeRef.current) };
      setBlinkMarkers((current) => [...current, marker]);
      const timeout = globalThis.setTimeout(() => {
        setBlinkMarkers((current) => current.filter((item) => item.id !== id));
        blinkTimeoutsRef.current.delete(timeout);
      }, 700);
      blinkTimeoutsRef.current.add(timeout);
    },
    [sizeRef]
  );

  return { blinkMarkers, addBlinkMarker };
}
